/**
 * Project list page — `GET /projects/tenant/{tenantId}`.
 *
 * The reference implementation of the CoOrdio list-page pattern in Cortex. Every other list page
 * should follow this shape: `ShellHeader` for the title and primary action, `CordioAbpPagedDataTable`
 * for the grid, one `DeleteDialog` driven by `useDeleteDialog`, and a BEM block named after the page.
 *
 * WHY the grid owns paging, search, sorting and filtering rather than the page: those four used to be
 * page state (`pageNumber`, `searchTerm`, a `useEffect` that refetched on change), which meant every
 * list page re-implemented them slightly differently and none of them carried the `data-testid`
 * attributes the E2E grid checklist (GRID-01…21) asserts against. The grid supplies all of it,
 * `urlSync`-style state included, so the page is left with the parts that are genuinely its own:
 * which columns to show, what a row action does, and where a row click navigates.
 *
 * The `listVersion` counter is the one piece of refetch state the page keeps. Bumping it rebuilds
 * `dataSource`, which is how the grid is told to reload after a mutation it did not perform.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';
import { Plus, Trash2 } from 'lucide-react';

import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { ProjectForm } from '../components/projects/ProjectForm';
import { projectService } from '../services/projectService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { ProjectDto } from '../types/project';

import './ProjectListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice over: the grid writes it to the toolbar's `paramKey`, AND it builds
 * each tab's test id as `cordio-datatable-status-tab-<value>`. GRID-13 asserts on
 * `-all` / `-true` / `-false`, so the All tab must carry the literal `'all'` — an empty value would
 * emit `cordio-datatable-status-tab-` and the checklist would select nothing.
 *
 * That is why `paramKey` below is the synthetic `status` rather than `isActive`: `'all'` is a UI
 * concept, not a value the API accepts, so {@link toProjectQuery} maps it (and drops it for All).
 * CoordioCore does the same thing with its `quickFilter` key.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Turns the grid's query into the one `projectService.getProjects` accepts: the synthetic `status`
 * tab value becomes `isActive`, and All becomes "no filter at all" rather than a third state.
 */
function toProjectQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  if (status === 'true' || status === 'false') {
    return { ...rest, isActive: status === 'true' };
  }
  return rest;
}

export const ProjectListPage = () => {
  const navigate = useNavigate();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const setProject = useContextStore((s) => s.setProject);
  const refreshSwitcherProjects = useContextStore((s) => s.loadProjects);
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<ProjectDto>();

  const tenantId = currentTenant?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<ProjectDto>((query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (!tenantId) throw new Error('No workspace selected');
        return projectService.getProjects(tenantId, toProjectQuery(query));
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ProjectDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '28%', sortable: true, clickable: true },
      { field: 'slug', header: 'Slug', type: 'text', width: '18%', sortable: true },
      {
        field: 'description',
        header: 'Description',
        type: 'text',
        width: '38%',
        maxLength: 120,
        showReadMore: true,
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '16%',
        // A Chip rather than the built-in boolean cell: "Active/Inactive" is the vocabulary the
        // rest of the product uses, and it matches the status tabs above the grid.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search projects by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Project status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        { key: 'name', label: 'Name', type: 'text', placeholder: 'Project name' },
        { key: 'slug', label: 'Slug', type: 'text', placeholder: 'Project slug' },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ProjectDto>>>(
    () => [
      {
        icon: 'square-pen',
        tooltip: 'Edit project',
        onClick: (row) => navigate(`${ROUTES.PROJECTS}/${row.id}/edit`),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete project',
        className: 'project-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem || !tenantId) return;

    await projectService.deleteProject(pendingItem.id, tenantId);
    showNotification('Project deleted successfully', 'success');

    // The deleted project may be the one selected in the shell's project switcher; leaving it
    // selected would scope every later request to a project that no longer exists.
    if (currentProject?.id === pendingItem.id) {
      setProject(null);
    }

    setListVersion((v) => v + 1);
    void refreshSwitcherProjects(tenantId);
  }, [pendingItem, tenantId, showNotification, currentProject, setProject, refreshSwitcherProjects]);

  const handleFormClose = useCallback(() => {
    setCreateOpen(false);
    setListVersion((v) => v + 1);
    if (tenantId) void refreshSwitcherProjects(tenantId);
  }, [tenantId, refreshSwitcherProjects]);

  if (!tenantId) {
    return (
      <div className="project-list" data-testid="project-list-page">
        <ShellHeader title="Projects" description="Workspaces group the agents, tools and sessions that belong together." data-testid="project-list-header" />
        <EmptyState
          title="No workspace selected"
          description="Choose a workspace from the user menu to see its projects."
          data-testid="project-empty-state"
        />
      </div>
    );
  }

  return (
    <div className="project-list" data-testid="project-list-page">
      <ShellHeader
        title="Projects"
        description={`Projects in ${currentTenant.name} — each one scopes its own agents, tools and sessions.`}
        data-testid="project-list-header"
        actions={
          <Button type="button" variant="default" size="sm" className="gap-2" data-testid="add-project-btn" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            New Project
          </Button>
        }
      />

      <div data-testid="project-list-grid">
        <CordioAbpPagedDataTable<ProjectDto>
          className="project-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.PROJECTS}/${row.id}/edit`)}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete project?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="The project's agents, tools and sessions lose their grouping. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />

      {createOpen && <ProjectForm project={null} open={createOpen} onClose={handleFormClose} />}
    </div>
  );
};
